import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Drawer, Button, Space, Modal } from "antd";
import Pagination from "@mui/material/Pagination";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faEye } from "@fortawesome/free-solid-svg-icons";

import TobNavBar from "../../DashBaord/Top_navbar";
import ShowData from "../../../components/ShowData";

import moment from "moment";
import SkeltonList from "../../../components/SkeltonEffect/list";
import { list } from "../../../controllers/V1/transactions/transactionController";
import { getPage, formatProjectDate } from "../../../controllers/common";
import SelectUsers from "../../../components/SelectUsers";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function TransactionList() {
  const targetRef = useRef(null);

  // Tabs
  const [tabValue, setTabValue] = useState(0);
  const [recordTypeTab, setRecordTypeTab] = useState("");
  const handleTabChange = (event, newValue) => setTabValue(newValue);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [ALLLISTDATA, setListData] = useState([]);
  const [TOTALPAGES, setTotalPages] = useState(1);
  const [LIMIT, setLimit] = useState(10);
  const [SKIP, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Filter drawer
  const [isFilterShow, setFilterShow] = useState(false);
  const handleFilterDrawer = () => setFilterShow(!isFilterShow);

  // Filter input refs
  const refSearch   = useRef(null);
  const refFromDate = useRef(null);
  const refToDate   = useRef(null);
  const refStatus   = useRef(null);

  // Filter state
  const [filter, setFilter] = useState({
    search: "",
    from_date: "",
    to_date: "",
    status: "",
    debit_user: "",
    credit_user: "",
    from_user: "",
    invoice_data: "",
  });

  // User selectors inside filter drawer
  const [debitUserData,   setDebitUserData]   = useState({});
  const [creditUserData,  setCreditUserData]  = useState({});
  const [fromUserData,    setFromUserData]    = useState({});

  // Detail modal
  const [isDetailOpen, setDetailOpen] = useState(false);
  const [detailData,   setDetailData] = useState(null);

  /*********************************************************
   *  Fetch list
   *********************************************************/
  const getList = async () => {
    try {
      setIsLoading(true);
      setListData([]);

      const options = {
        type: "",
        condition: {},
        sort: { _id: -1 },
        skip: SKIP || 0,
        limit: LIMIT || 10,
        ...(filter.from_date ? { from_date: filter.from_date } : null),
        ...(filter.to_date   ? { to_date:   filter.to_date   } : null),
        ...(recordTypeTab    ? { record_type: recordTypeTab  } : null),
        ...(filter.status    ? { status:      filter.status  } : null),
        ...(filter.debit_user  ? { debit_user:  filter.debit_user  } : null),
        ...(filter.credit_user ? { credit_user: filter.credit_user } : null),
        ...(filter.from_user   ? { from_user:   filter.from_user   } : null),
        ...(filter.invoice_data ? { invoice_data: filter.invoice_data } : null),
        ...(filter.search      ? { search:      filter.search      } : null),
      };

      const listData = await list(options);
      if (listData.status === true) {
        if (listData.result && listData.result.length > 0) {
          setListData(listData.result);
          setTotalPages(getPage(listData.count, LIMIT));
        } else {
          setListData([]);
          setTotalPages(getPage(1));
        }
      } else {
        setListData([]);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    setSkip((newPage - 1) * LIMIT);
    targetRef.current.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    getList();
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth" });
    }
    document.title = "Crown King || Transactions";
  }, [currentPage, recordTypeTab, filter, LIMIT]);

  /*********************************************************
   *  Filter handlers
   *********************************************************/
  const handleFilterApply = (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    setFilter((prev) => ({
      ...prev,
      search:    form.get("search")    || "",
      from_date: form.get("from_date") || "",
      to_date:   form.get("to_date")   || "",
      status:    form.get("status")    || "",
      debit_user:  debitUserData?.bind_person_id  || "",
      credit_user: creditUserData?.bind_person_id || "",
      from_user:   fromUserData?.bind_person_id   || "",
    }));
    setSkip(0);
    setCurrentPage(1);
    handleFilterDrawer();
  };

  const handleFilterReset = () => {
    setFilter({
      search: "", from_date: "", to_date: "",
      status: "",
      debit_user: "", credit_user: "", from_user: "", invoice_data: "",
    });
    setDebitUserData({});
    setCreditUserData({});
    setFromUserData({});
    if (refSearch.current)   refSearch.current.value   = "";
    if (refFromDate.current) refFromDate.current.value = "";
    if (refToDate.current)   refToDate.current.value   = "";
    if (refStatus.current)   refStatus.current.value   = "";
    setSkip(0);
    setCurrentPage(1);
  };

  /*********************************************************
   *  Detail modal
   *********************************************************/
  const handleViewDetail = (item) => {
    setDetailData(item);
    setDetailOpen(true);
  };

  /*********************************************************
   *  Helpers
   *********************************************************/
  const userName = (userObj) => {
    if (!userObj) return "—";
    if (typeof userObj === "string") return userObj;
    return `${userObj.users_name || ""} ${userObj.last_name || ""}`.trim() || userObj.users_mobile || "—";
  };

  const recordTypeBadge = (type) => {
    const color = type === "Debit"
      ? { backgroundColor: "#C51020", color: "white" }
      : { backgroundColor: "rgb(17 174 87)", color: "white" };
    return (
      <Button type="primary" style={color} size="small">
        {type || "—"}
      </Button>
    );
  };

  const statusBadge = (status) => {
    const color = status === "Success" || status === "success"
      ? { backgroundColor: "rgb(17 174 87)", color: "white" }
      : status === "Failed" || status === "Pending"
      ? { backgroundColor: "#C51020", color: "white" }
      : { backgroundColor: "#888", color: "white" };
    return (
      <Button type="primary" style={color} size="small">
        {status || "—"}
      </Button>
    );
  };

  return (
    <>
      <div className="container-fluid" ref={targetRef}>
        <TobNavBar title={"Transactions"} />

        <div className="inventory_tabs_main_div">
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <div className="inventory_all_tabs">
                <div className="all_tabs_value">
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="transaction tabs"
                    className="item_tabs_main_container"
                  >
                    <Tab label="All"    className="tabs_main_contnet" {...a11yProps(0)} onClick={() => setRecordTypeTab("")} />
                    <Tab label="Debit"  className="tabs_main_contnet" {...a11yProps(1)} onClick={() => setRecordTypeTab("Debit")} />
                    <Tab label="Credit" className="tabs_main_contnet" {...a11yProps(2)} onClick={() => setRecordTypeTab("Credit")} />
                  </Tabs>
                </div>

                <div className="fa_filter_rating_review filter_img_and_add_btn_main">
                  <ShowData setLimit={setLimit} limit={LIMIT} />
                  <div className="inventory_filter_div" onClick={handleFilterDrawer}>
                    <FontAwesomeIcon icon={faFilter} />
                    <span className="filter_btn_invontry_rating_review" style={{ cursor: "pointer" }}>
                      &nbsp;Filter
                    </span>
                  </div>
                </div>
              </div>
            </Box>

            <TabPanel value={tabValue} index={tabValue}>
              <div id="content-wrapper" className="d-flex flex-column">
                <div className="card shadow mb-4">
                  <div className="card-body pt-0">
                    <div className="table-responsive">
                      <table
                        className="table table-bordered"
                        id="dataTable"
                        width="100%"
                        cellSpacing="0"
                      >
                        <thead>
                          <tr>
                            <th>Sl. No.</th>
                            <th>Amount</th>
                            <th>Before Amt.</th>
                            <th>After Amt.</th>
                            <th>Record Type</th>
                            <th>Narration</th>
                            <th>Remarks</th>
                            <th>Debit User</th>
                            <th>Credit User</th>
                            <th>From User</th>
                            <th>Invoice</th>
                            <th>Status</th>
                            <th>Created Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ALLLISTDATA.length > 0 ? (
                            ALLLISTDATA.map((item, index) => (
                              <tr key={item._id || index}>
                                <td>{SKIP + index + 1}</td>
                                <td>{item?.amount != null ? Math.floor(item.amount * 100) / 100 : "—"}</td>
                                <td>{item?.before_amount != null ? Math.floor(item.before_amount * 100) / 100 : "—"}</td>
                                <td>{item?.after_amount  != null ? Math.floor(item.after_amount  * 100) / 100 : "—"}</td>
                                <td>{recordTypeBadge(item?.record_type)}</td>
                                <td style={{ maxWidth: "180px", wordBreak: "break-word" }}>{item?.narration || "—"}</td>
                                <td style={{ maxWidth: "150px", wordBreak: "break-word" }}>{item?.remarks  || "—"}</td>
                                <td>{userName(item?.debit_user)}</td>
                                <td>{userName(item?.credit_user)}</td>
                                <td>{userName(item?.from_user)}</td>
                                <td>
                                  {item?.invoice_data
                                    ? item.invoice_data?.invoice_id || item.invoice_data?.invoice_date || "—"
                                    : "—"}
                                </td>
                                <td>{statusBadge(item?.status)}</td>
                                <td>
                                  {item?.createdAt
                                    ? moment(formatProjectDate(item.createdAt)).format("DD-MM-YYYY hh:mm A")
                                    : "—"}
                                </td>
                                <td>
                                  <Button
                                    type="link"
                                    size="small"
                                    onClick={() => handleViewDetail(item)}
                                    title="View"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : isLoading ? (
                            <SkeltonList row={5} col={14} />
                          ) : (
                            <tr>
                              <td colSpan="14" className="img-center">
                                No data found!
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      <div className="table_pagination_sales_order">
                        <Pagination count={TOTALPAGES} onChange={handlePageChange} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>
          </Box>
        </div>
      </div>

      {/* ─── Filter Drawer ───────────────────────────────────────────── */}
      <Drawer
        title="Filter Transactions"
        placement="right"
        width={480}
        onClose={handleFilterDrawer}
        open={isFilterShow}
        extra={
          <Space>
            <Button onClick={handleFilterDrawer}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleFilterReset}
              style={{ backgroundColor: "var(--brand-red)", color: "white" }}
            >
              Reset
            </Button>
          </Space>
        }
      >
        <form id="filter_form" onSubmit={handleFilterApply}>
          <div className="row">

            <div className="form-group col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-3">
              <label className="all_section_for_label_main_class">Search (Narration / Remarks):</label>
              <input
                type="text"
                name="search"
                placeholder="Search in narration or remarks"
                className="form-control store_input_field"
                ref={refSearch}
                defaultValue={filter.search}
              />
            </div>

            <div className="form-group col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-3">
              <label className="all_section_for_label_main_class">From Date:</label>
              <input
                type="datetime-local"
                name="from_date"
                className="form-control store_input_field"
                ref={refFromDate}
                defaultValue={filter.from_date}
              />
            </div>

            <div className="form-group col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-3">
              <label className="all_section_for_label_main_class">To Date:</label>
              <input
                type="datetime-local"
                name="to_date"
                className="form-control store_input_field"
                ref={refToDate}
                defaultValue={filter.to_date}
              />
            </div>

            <div className="form-group col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-3">
              <label className="all_section_for_label_main_class">Status:</label>
              <input
                type="text"
                name="status"
                placeholder="e.g. Success, Failed"
                className="form-control store_input_field"
                ref={refStatus}
                defaultValue={filter.status}
              />
            </div>

            <div className="form-group col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-3">
              <label className="all_section_for_label_main_class">Debit User:</label>
              <SelectUsers setAddEditData={setDebitUserData} />
            </div>

            <div className="form-group col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-3">
              <label className="all_section_for_label_main_class">Credit User:</label>
              <SelectUsers setAddEditData={setCreditUserData} />
            </div>

            <div className="form-group col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-3">
              <label className="all_section_for_label_main_class">From User:</label>
              <SelectUsers setAddEditData={setFromUserData} />
            </div>

            <div className="form-group col-lg-12 col-md-12 col-sm-12 col-xs-12 mt-2">
              <button
                type="submit"
                className="form-control btn btn-black"
                style={{ backgroundColor: "var(--brand-red-dark)", color: "white" }}
              >
                Apply
              </button>
            </div>
          </div>
        </form>
      </Drawer>

      {/* ─── Detail Modal ────────────────────────────────────────────── */}
      <Modal
        title="Transaction Detail"
        open={isDetailOpen}
        onCancel={() => { setDetailOpen(false); setDetailData(null); }}
        footer={[
          <Button key="close" onClick={() => { setDetailOpen(false); setDetailData(null); }}>
            Close
          </Button>,
        ]}
        width={640}
      >
        {detailData ? (
          <div className="table-responsive">
            <table className="table table-bordered">
              <tbody>
                <tr><th style={{ width: "38%" }}>Amount</th><td>{detailData?.amount != null ? Math.floor(detailData.amount * 100) / 100 : "—"}</td></tr>
                <tr><th>Before Amount</th><td>{detailData?.before_amount != null ? Math.floor(detailData.before_amount * 100) / 100 : "—"}</td></tr>
                <tr><th>After Amount</th><td>{detailData?.after_amount  != null ? Math.floor(detailData.after_amount  * 100) / 100 : "—"}</td></tr>
                <tr><th>Record Type</th><td>{recordTypeBadge(detailData?.record_type)}</td></tr>
                <tr><th>Status</th><td>{statusBadge(detailData?.status)}</td></tr>
                <tr><th>Narration</th><td>{detailData?.narration || "—"}</td></tr>
                <tr><th>Remarks</th><td>{detailData?.remarks    || "—"}</td></tr>
                <tr><th>Other Expenses</th><td>{detailData?.other_expenses != null ? detailData.other_expenses : "—"}</td></tr>
                <tr><th>Is Reverse</th><td>{detailData?.is_reverse || "—"}</td></tr>
                <tr>
                  <th>Debit User</th>
                  <td>
                    {detailData?.debit_user
                      ? `${userName(detailData.debit_user)} (${detailData.debit_user?.users_mobile || ""})`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <th>Credit User</th>
                  <td>
                    {detailData?.credit_user
                      ? `${userName(detailData.credit_user)} (${detailData.credit_user?.users_mobile || ""})`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <th>From User</th>
                  <td>
                    {detailData?.from_user
                      ? `${userName(detailData.from_user)} (${detailData.from_user?.users_mobile || ""})`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <th>Invoice</th>
                  <td>
                    {detailData?.invoice_data
                      ? `ID: ${detailData.invoice_data?.invoice_id || "—"} | Date: ${detailData.invoice_data?.invoice_date ? moment(detailData.invoice_data.invoice_date).format("DD-MM-YYYY") : "—"}`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <th>Created Date</th>
                  <td>
                    {detailData?.createdAt
                      ? moment(formatProjectDate(detailData.createdAt)).format("DD-MM-YYYY hh:mm A")
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p>No data.</p>
        )}
      </Modal>
    </>
  );
}
